/**
 * «Revisar el modelo» (OPUS ci, solo staff): el equivalente para IFC de «Revisar el dibujo». Convierte lo que hoy son porcentajes del
 * chequeo («X % sin medida») en avisos con LA LISTA de piezas, y agrega lo que cambia cantidades: piezas repetidas una encima de otra,
 * piezas desmedidas frente a su tipo, piezas lejos del resto y piezas con la misma identificación.
 *
 * $0 y determinista, sobre el inventario que el navegador ya arma (`PlanoCapa.instancias[]`: centro, dims, medidas, identificación).
 * Sin listas de palabras ni tablas: todo sale de la coherencia interna del modelo (lo que dice su tipo, la mediana de sus iguales,
 * el centro de la obra). Umbrales `diag_ifc_*` en Calibración. No cambia cantidades: solo avisa y señala. Spec:
 * `docs/_archivo/internos/spec-diagnostico-modelo-ifc.md`.
 */
import type { DecisionDeCapa, PlanoCapa, PlanoInventario } from './domain.js';
import { CLASE_IFC_GENERICA, tieneSuMedida, type ParametrosChequeoPlano } from './chequeoDelPlano.js';
import { nombreLegibleTipoIfc } from './etiquetasIfc.js';
import type { GravedadDeAviso } from './diagnosticoDelDibujo.js';

export type ClaveDeAvisoDelModelo =
  | 'repetidas'
  | 'misma_identificacion'
  | 'sin_medida'
  | 'desmedidas'
  | 'lejos'
  | 'genericas'
  | 'sin_tipo'
  | 'sin_piso'
  | 'sin_material';

export interface AvisoDelModelo {
  clave: ClaveDeAvisoDelModelo;
  gravedad: GravedadDeAviso;
  titulo: string;
  quePasa: string;
  porQueAfecta: string;
  queHacer: string;
  /** «Toca N piezas». */
  piezas: number;
  /** Piezas para «Ver en el modelo» (su número en el visor), con tope; `piezas` cuenta todas. */
  ids: number[];
  /** Los tipos que toca (su etiqueta, `PlanoCapa.capa`). */
  tipos: string[];
  /** Cuántas de las piezas no se pueden señalar (el modelo no les da forma). */
  sinForma?: number;
  /** Lo que se cuenta de más (o con 0) en la hoja, si se puede medir. */
  impacto?: { magnitud: 'pza' | 'ml' | 'm2' | 'm3'; cantidad: number; pctDeLaCapa?: number } | null;
  /** Renglones extra del detalle, ya redactados. */
  notas?: string[];
}

export interface DiagnosticoDelModelo {
  /** Orden: gravedad (alta → info) y luego piezas, de más a menos. */
  avisos: AvisoDelModelo[];
  piezasRevisadas: number;
}

export interface EntradaDiagnosticoModelo {
  inventario: PlanoInventario;
  /** Decisión del tipo por su etiqueta (la del usuario o la de su clase). Ausente o null = se cotiza (no se esconde un aviso). */
  decisionCapa?: (capa: string) => DecisionDeCapa | null;
  /** Obra del modelo de cada pieza, si el modelo declara varias: un centro por obra (un conjunto de edificios no se marca). */
  obraDe?: (id: number) => number | undefined;
}

/** Tope de memoria y de pantalla, no regla de negocio. */
export const TOPE_PIEZAS_POR_AVISO = 20000;

type Instancia = NonNullable<PlanoCapa['instancias']>[number];
type Magnitud = 'area' | 'longitud' | 'volumen' | 'conteo';

interface Pieza {
  capa: PlanoCapa;
  nombre: string;
  i: Instancia;
  id: number;
}

const fmt = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 1 });
const piezasTxt = (n: number) => (n === 1 ? '1 pieza' : `${fmt(n)} piezas`);
const lista = (xs: string[], max = 3) => {
  const u = [...new Set(xs)];
  return u.length <= max ? u.join(', ') : `${u.slice(0, max).join(', ')} y ${u.length - max} más`;
};
const topeIds = (ids: number[]) => (ids.length > TOPE_PIEZAS_POR_AVISO ? ids.slice(0, TOPE_PIEZAS_POR_AVISO) : ids);

const sePuedeCotizar = (e: EntradaDiagnosticoModelo, capa: string) => e.decisionCapa?.(capa) !== 'ruido';

/** Las piezas del modelo (las de las capas con clase). */
function piezasDelModelo(inv: PlanoInventario): Pieza[] {
  const out: Pieza[] = [];
  for (const c of inv.capas) {
    if (c.categoria == null) continue;
    const nombre = nombreLegibleTipoIfc(c);
    for (const i of c.instancias ?? []) out.push({ capa: c, nombre, i, id: i.id });
  }
  return out;
}

const UNIDAD_DE: Record<Magnitud, 'pza' | 'm2' | 'ml' | 'm3'> = { conteo: 'pza', area: 'm2', longitud: 'ml', volumen: 'm3' };

/** La medida por la que se presupuesta la pieza (su valor), o undefined si no manda ninguna. */
function medidaQueManda(p: Pieza): number | undefined {
  const m = p.capa.medidas?.manda;
  if (!m || m === 'conteo') return undefined;
  const v = p.i[m];
  return typeof v === 'number' && v > 0 ? v : undefined;
}

// ───────────────────────────── utilidades puras ─────────────────────────────

/** Las medidas propias de un tipo (las que NO varían entre piezas del mismo tipo), de mayor a menor. */
export function medidaPropiaDelTipo(dims: { largo: number; ancho: number; alto: number } | undefined, manda: Magnitud | undefined): number[] {
  if (!dims) return [];
  const d = [dims.largo, dims.ancho, dims.alto].filter((x) => Number.isFinite(x)).sort((a, b) => b - a);
  if (d.length < 3) return [];
  if (manda === 'area') return [d[2]!]; // espesor
  if (manda === 'longitud') return [d[1]!, d[2]!]; // sección
  return [d[0]!, d[1]!, d[2]!];
}

interface PiezaParaGrupo {
  clase: string;
  centro: readonly number[];
  dims: readonly number[];
  medida?: number;
}

const cercanas = (a: number, b: number, tolRel: number) => Math.abs(a - b) <= tolRel * Math.max(Math.abs(a), Math.abs(b), 1e-9);

/**
 * Grupos de piezas coincidentes: misma clase, centros a menos de `tolM` y medidas (ordenadas de mayor a menor, para no depender de la
 * orientación, y la que manda) iguales dentro de `tolRel`. Componentes conexas. Índices sobre la lista de entrada; solo grupos de 2+.
 */
export function gruposCoincidentes(piezas: readonly PiezaParaGrupo[], tolM: number, tolRel: number): number[][] {
  const tol = tolM > 0 ? tolM : 0.01;
  // Una rejilla por clase, con la celda como número (los choques de la mezcla solo agregan candidatos: la distancia se comprueba).
  const celdasDe = new Map<string, Map<number, number[]>>();
  const cel = (v: number) => Math.floor(v / tol);
  const mezcla = (a: number, b: number, c: number) => (Math.imul(a, 73856093) ^ Math.imul(b, 19349663) ^ Math.imul(c, 83492791)) | 0;
  piezas.forEach((p, k) => {
    let celdas = celdasDe.get(p.clase);
    if (!celdas) celdasDe.set(p.clase, (celdas = new Map()));
    const key = mezcla(cel(p.centro[0]!), cel(p.centro[1]!), cel(p.centro[2]!));
    const g = celdas.get(key);
    if (g) g.push(k);
    else celdas.set(key, [k]);
  });
  const padre = piezas.map((_, k) => k);
  const raiz = (k: number): number => {
    while (padre[k] !== k) {
      padre[k] = padre[padre[k]!]!;
      k = padre[k]!;
    }
    return k;
  };
  const igualesEnForma = (a: PiezaParaGrupo, b: PiezaParaGrupo) => {
    if (a.dims.length !== b.dims.length) return false;
    const da = [...a.dims].sort((x, y) => y - x);
    const db = [...b.dims].sort((x, y) => y - x);
    if (!da.every((x, n) => cercanas(x, db[n]!, tolRel))) return false;
    return a.medida == null || b.medida == null || cercanas(a.medida, b.medida, tolRel);
  };
  piezas.forEach((p, k) => {
    const celdas = celdasDe.get(p.clase)!;
    const ix = cel(p.centro[0]!);
    const iy = cel(p.centro[1]!);
    const iz = cel(p.centro[2]!);
    for (let dx = -1; dx <= 1; dx++)
      for (let dy = -1; dy <= 1; dy++)
        for (let dz = -1; dz <= 1; dz++) {
          for (const j of celdas.get(mezcla(ix + dx, iy + dy, iz + dz)) ?? []) {
            if (j <= k) continue;
            const q = piezas[j]!;
            if (Math.hypot(p.centro[0]! - q.centro[0]!, p.centro[1]! - q.centro[1]!, p.centro[2]! - q.centro[2]!) > tol) continue;
            if (!igualesEnForma(p, q)) continue;
            const a = raiz(k);
            const b = raiz(j);
            if (a !== b) padre[Math.max(a, b)] = Math.min(a, b);
          }
        }
  });
  const grupos = new Map<number, number[]>();
  piezas.forEach((_, k) => {
    const r = raiz(k);
    const g = grupos.get(r);
    if (g) g.push(k);
    else grupos.set(r, [k]);
  });
  return [...grupos.values()].filter((g) => g.length >= 2);
}

// ───────────────────────────── chequeos ─────────────────────────────

/** M1 · Piezas repetidas una encima de otra (cada copia suma). Hasta 2 avisos: del mismo tipo / de tipos distintos. */
export function piezasRepetidasModelo(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): AvisoDelModelo[] {
  const todas = piezasDelModelo(e.inventario).filter((x) => x.i.centro && x.i.dims);
  const grupos = gruposCoincidentes(
    todas.map((x) => ({
      clase: x.capa.categoria!,
      centro: x.i.centro!,
      dims: [x.i.dims!.largo, x.i.dims!.ancho, x.i.dims!.alto],
      medida: medidaQueManda(x),
    })),
    p.diag_ifc_duplicado_tol_m,
    p.diag_ifc_duplicado_tol_rel,
  );
  const mismo = { piezas: 0, ids: [] as number[], tipos: [] as string[], cotiza: false, suma: new Map<Magnitud, number>() };
  const distintos = { piezas: 0, ids: [] as number[], tipos: [] as string[], cotiza: false, grupos: 0 };
  for (const g of grupos) {
    const ps = g.map((k) => todas[k]!).sort((a, b) => a.id - b.id);
    const extras = ps.slice(1); // la «original» es la de menor número: es determinista
    const mismoTipo = ps.every((x) => x.capa.capa === ps[0]!.capa.capa);
    const cotiza = ps.some((x) => sePuedeCotizar(e, x.capa.capa));
    if (mismoTipo) {
      mismo.piezas += extras.length;
      mismo.ids.push(...extras.map((x) => x.id));
      mismo.tipos.push(ps[0]!.nombre);
      mismo.cotiza ||= cotiza;
      const m: Magnitud = ps[0]!.capa.medidas?.manda ?? 'conteo';
      const valor = m === 'conteo' ? extras.length : extras.reduce((s, x) => s + (medidaQueManda(x) ?? 0), 0);
      mismo.suma.set(m, (mismo.suma.get(m) ?? 0) + valor);
    } else {
      distintos.piezas += extras.length;
      distintos.grupos++;
      distintos.ids.push(...ps.map((x) => x.id));
      distintos.tipos.push(...ps.map((x) => x.nombre));
      distintos.cotiza ||= cotiza;
    }
  }
  const out: AvisoDelModelo[] = [];
  if (mismo.piezas > 0) {
    const m = mismo.suma.size === 1 ? [...mismo.suma.keys()][0]! : null;
    const cantidad = m ? mismo.suma.get(m)! : null;
    out.push({
      clave: 'repetidas',
      gravedad: mismo.cotiza ? 'alta' : 'info',
      titulo: `${piezasTxt(mismo.piezas)} repetida${mismo.piezas === 1 ? '' : 's'} una encima de otra (${lista(mismo.tipos)})`,
      quePasa: 'Hay piezas del mismo tipo dos veces en el mismo lugar, con la misma forma.',
      porQueAfecta: `Cada copia se cuenta: en la hoja saldrían ${piezasTxt(mismo.piezas)} de más${m && m !== 'conteo' && cantidad != null ? ` (${fmt(cantidad)} ${m === 'area' ? 'm²' : m === 'longitud' ? 'm' : 'm³'})` : ''}.`,
      queHacer: 'Bórralas en tu programa de modelado y vuelve a subir el modelo, o quítalas de la cantidad en la hoja.',
      piezas: mismo.piezas,
      ids: topeIds(mismo.ids),
      tipos: [...new Set(mismo.tipos)],
      impacto: m && cantidad != null ? { magnitud: UNIDAD_DE[m], cantidad } : null,
    });
  }
  if (distintos.piezas > 0) {
    out.push({
      clave: 'repetidas',
      gravedad: distintos.cotiza ? 'media' : 'info',
      titulo: `${distintos.grupos === 1 ? '1 lugar con' : `${fmt(distintos.grupos)} lugares con`} dos tipos distintos encima`,
      quePasa: 'Hay piezas de tipos distintos en el mismo lugar, con la misma forma.',
      porQueAfecta: 'Las dos se cuentan, cada una en su renglón.',
      queHacer: 'Revisa cuál es la buena y borra la otra, o corrige su tipo en tu programa de modelado.',
      piezas: distintos.ids.length,
      ids: topeIds(distintos.ids),
      tipos: [...new Set(distintos.tipos)],
      impacto: null,
    });
  }
  return out;
}

/** M5 · Dos piezas con la misma identificación (el estándar exige que sea única). */
export function mismaIdentificacion(e: EntradaDiagnosticoModelo, _p: ParametrosChequeoPlano): AvisoDelModelo | null {
  const porGid = new Map<string, Pieza[]>();
  for (const x of piezasDelModelo(e.inventario)) {
    if (!x.i.gid) continue;
    const g = porGid.get(x.i.gid);
    if (g) g.push(x);
    else porGid.set(x.i.gid, [x]);
  }
  const ids: number[] = [];
  const tipos: string[] = [];
  for (const g of porGid.values()) {
    if (new Set(g.map((x) => x.id)).size < 2) continue;
    for (const x of g) {
      ids.push(x.id);
      tipos.push(x.nombre);
    }
  }
  if (!ids.length) return null;
  return {
    clave: 'misma_identificacion',
    gravedad: 'media',
    titulo: `${piezasTxt(ids.length)} ${ids.length === 1 ? 'comparte' : 'comparten'} la misma identificación`,
    quePasa: 'Hay piezas distintas con el mismo código de identificación, y debería ser único.',
    porQueAfecta: 'Al comparar con otra versión del modelo, o al recordar el concepto de una pieza, una se confunde con la otra. No cambia las cantidades.',
    queHacer: 'En tu programa de modelado, vuelve a exportar el modelo; si copiaste piezas, que el programa les dé una identificación nueva.',
    piezas: ids.length,
    ids: topeIds(ids),
    tipos: [...new Set(tipos)],
    impacto: null,
  };
}

/** M2 · Piezas sin la medida por la que se presupuestan (entrarían a la hoja con 0). */
export function piezasSinMedida(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): AvisoDelModelo | null {
  const todas = piezasDelModelo(e.inventario);
  const sin = todas.filter((x) => !tieneSuMedida(x.i, x.capa.medidas?.manda));
  if (!sin.length) return null;
  const cotiza = sin.some((x) => sePuedeCotizar(e, x.capa.capa));
  const pct = (sin.length / Math.max(1, todas.length)) * 100;
  const sinForma = sin.filter((x) => !x.i.centro).length;
  return {
    clave: 'sin_medida',
    gravedad: cotiza ? (pct > p.chequeo_aviso_sin_cantidades_pct ? 'alta' : 'media') : 'info',
    titulo: `${piezasTxt(sin.length)} no ${sin.length === 1 ? 'tiene' : 'tienen'} la medida por la que se presupuestan`,
    quePasa: 'Ni el modelo dice su medida ni se pudo sacar de su forma.',
    porQueAfecta: 'Entran a la hoja con cantidad 0, marcadas para que la escribas.',
    queHacer: 'Mídelas a mano o agrégales su forma en tu programa de modelado.',
    piezas: sin.length,
    ids: topeIds(sin.filter((x) => x.i.centro).map((x) => x.id)),
    tipos: [...new Set(sin.map((x) => x.nombre))],
    sinForma,
    impacto: { magnitud: 'pza', cantidad: sin.length },
    ...(sinForma > 0 ? { notas: [`${piezasTxt(sinForma)} de ellas no ${sinForma === 1 ? 'tiene' : 'tienen'} forma en el modelo: no se pueden señalar.`] } : {}),
  };
}

const mediana = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
};

/** M3 · Piezas que no miden como las demás de su tipo (solo tipos que el modelo declara: los agrupados por nosotros son heterogéneos). */
export function piezasDesmedidas(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): AvisoDelModelo | null {
  const ids: number[] = [];
  const tipos: string[] = [];
  let cotiza = false;
  let fuerte = false;
  let impactoSuma = 0;
  let impactoMag: Magnitud | null = null;
  let componente = '';
  for (const c of e.inventario.capas) {
    if (c.categoria == null) continue;
    const origen = c.tipoDelModelo?.origen;
    if (origen !== 'tipo' && origen !== 'objectType') continue;
    const manda = c.medidas?.manda;
    const piezas = (c.instancias ?? []).filter((i) => i.dims);
    if (piezas.length < p.diag_ifc_tipo_min_piezas) continue;
    const medidas = piezas.map((i) => medidaPropiaDelTipo(i.dims, manda));
    const n = medidas[0]?.length ?? 0;
    if (!n) continue;
    const med = Array.from({ length: n }, (_, k) => mediana(medidas.map((m) => m[k]!)));
    const atipicas = piezas.filter((_, idx) =>
      med.some((m, k) => {
        if (m <= p.diag_ifc_medida_cero_m) return false; // esa medida no se juzga
        const v = medidas[idx]![k]!;
        return v <= p.diag_ifc_medida_cero_m || Math.abs(Math.log10(v / m)) >= p.diag_ifc_atipica_decadas;
      }),
    );
    if (!atipicas.length) continue;
    const nombre = nombreLegibleTipoIfc(c);
    ids.push(...atipicas.map((i) => i.id));
    tipos.push(nombre);
    const se = sePuedeCotizar(e, c.capa);
    cotiza ||= se;
    componente = manda === 'area' ? 'espesor' : manda === 'longitud' ? 'sección' : 'tamaño';
    if (manda && manda !== 'conteo') {
      const peso = atipicas.reduce((s, i) => s + (i[manda] ?? 0), 0);
      const total = c.medidas?.[manda] ?? 0;
      impactoSuma += peso;
      impactoMag = manda;
      if (se && total > 0 && (peso / total) * 100 >= p.diag_ifc_atipica_capa_pct) fuerte = true;
    }
  }
  if (!ids.length) return null;
  return {
    clave: 'desmedidas',
    gravedad: cotiza ? (fuerte ? 'alta' : 'media') : 'info',
    titulo: `${piezasTxt(ids.length)} no ${ids.length === 1 ? 'mide' : 'miden'} como las demás de su tipo (${lista(tipos)})`,
    quePasa: `Su ${componente} es muy distinto al de las otras piezas del mismo tipo.`,
    porQueAfecta: 'Si está modelada en otra unidad o deformada, mete en la hoja una medida que no es.',
    queHacer: 'Revisa su tamaño en tu programa de modelado.',
    piezas: ids.length,
    ids: topeIds(ids),
    tipos: [...new Set(tipos)],
    impacto: impactoMag && impactoSuma > 0 ? { magnitud: UNIDAD_DE[impactoMag], cantidad: impactoSuma } : null,
  };
}

const percentil = (orden: number[], pc: number) => (orden.length ? orden[Math.min(orden.length - 1, Math.max(0, Math.round((pc / 100) * (orden.length - 1))))]! : 0);

/** M4 · Piezas lejos del resto de la obra (relativo al propio modelo, nunca en metros fijos). */
export function piezasLejos(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): AvisoDelModelo | null {
  const conCentro = piezasDelModelo(e.inventario).filter((x) => x.i.centro);
  if (conCentro.length < p.diag_ifc_min_piezas) return null;
  // Un centro por obra si el modelo declara varias; si no, uno solo.
  const grupos = new Map<number, Pieza[]>();
  for (const x of conCentro) {
    const o = e.obraDe?.(x.id) ?? -1;
    const g = grupos.get(o);
    if (g) g.push(x);
    else grupos.set(o, [x]);
  }
  const ids: number[] = [];
  const tipos: string[] = [];
  let cotiza = false;
  for (const g of grupos.values()) {
    if (g.length < p.diag_ifc_min_piezas) continue;
    const ejes = [0, 1, 2].map((k) => g.map((x) => x.i.centro![k]!).sort((a, b) => a - b));
    const lo = ejes.map((a) => percentil(a, p.diag_ifc_nucleo_pct));
    const hi = ejes.map((a) => percentil(a, 100 - p.diag_ifc_nucleo_pct));
    const lado = Math.max(hi[0]! - lo[0]!, hi[2]! - lo[2]!); // x y z son el plano horizontal; y es la vertical
    if (!(lado > 0)) continue;
    for (const x of g) {
      const c = x.i.centro!;
      const d = Math.hypot(...[0, 1, 2].map((k) => Math.max(0, lo[k]! - c[k]!, c[k]! - hi[k]!)));
      if (d <= p.diag_ifc_suelta_factor * lado) continue;
      ids.push(x.id);
      tipos.push(x.nombre);
      cotiza ||= sePuedeCotizar(e, x.capa.capa);
    }
  }
  if (!ids.length) return null;
  return {
    clave: 'lejos',
    gravedad: cotiza ? 'alta' : 'info',
    titulo: `${piezasTxt(ids.length)} lejos del resto de la obra`,
    quePasa: 'Hay piezas muy apartadas de todo lo demás.',
    porQueAfecta: 'Se cuentan como si fueran de esta obra; suelen ser piezas olvidadas o copiadas de otro proyecto.',
    queHacer: 'Revisa si son de la obra; si no, bórralas y vuelve a subir el modelo.',
    piezas: ids.length,
    ids: topeIds(ids),
    tipos: [...new Set(tipos)],
    impacto: { magnitud: 'pza', cantidad: ids.length },
  };
}

/** M6 · Lo que el modelo no dice de la pieza (un aviso por problema). Ninguno cambia la cantidad que manda: nunca son «altos». */
export function loQueElModeloNoDice(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): AvisoDelModelo[] {
  const todas = piezasDelModelo(e.inventario);
  const n = todas.length;
  const def: { clave: ClaveDeAvisoDelModelo; umbral: number; cuales: (x: Pieza) => boolean; titulo: (k: number) => string; quePasa: string; porQue: string; queHacer: string }[] = [
    {
      clave: 'genericas',
      umbral: p.chequeo_aviso_genericos_pct,
      cuales: (x) => x.capa.categoria === CLASE_IFC_GENERICA,
      titulo: (k) => `${piezasTxt(k)} ${k === 1 ? 'viene' : 'vienen'} como «genéricas»`,
      quePasa: 'El modelo no dice si son muro, viga o ventana.',
      porQue: 'No se pueden presupuestar con un concepto de su clase; están en «Revisar: genéricos».',
      queHacer: 'Asígnales su clase en tu programa de modelado.',
    },
    {
      clave: 'sin_tipo',
      umbral: p.chequeo_aviso_sin_tipo_pct,
      cuales: (x) => x.i.sinTipo === true,
      titulo: (k) => `${piezasTxt(k)} no ${k === 1 ? 'dice' : 'dicen'} de qué tipo ${k === 1 ? 'es' : 'son'}`,
      quePasa: 'Se agruparon por su nombre o su clase.',
      porQue: 'Revisa que estén juntas las que van juntas; la memoria de conceptos no las reconoce en otros proyectos.',
      queHacer: 'Dales su tipo en tu programa de modelado.',
    },
    {
      clave: 'sin_piso',
      umbral: p.chequeo_aviso_sin_nivel_pct,
      cuales: (x) => x.i.sinNivel === true,
      titulo: (k) => `${piezasTxt(k)} no ${k === 1 ? 'dice' : 'dicen'} en qué piso ${k === 1 ? 'está' : 'están'}`,
      quePasa: 'El modelo no las pone en ningún piso.',
      porQue: 'No se pueden separar por piso en la hoja.',
      queHacer: 'Colócalas en su piso en tu programa de modelado.',
    },
    {
      clave: 'sin_material',
      umbral: p.chequeo_aviso_sin_material_pct,
      cuales: (x) => x.i.sinMaterial === true,
      titulo: (k) => `${piezasTxt(k)} no ${k === 1 ? 'dice' : 'dicen'} de qué material ${k === 1 ? 'es' : 'son'}`,
      quePasa: 'No tienen material asociado.',
      porQue: 'Sin material no se pueden pesar (el acero, por ejemplo, se cobra por kilo).',
      queHacer: 'Asígnales su material en tu programa de modelado.',
    },
  ];
  const out: AvisoDelModelo[] = [];
  for (const d of def) {
    const ps = todas.filter(d.cuales);
    if (!ps.length) continue;
    const porciento = (ps.length / Math.max(1, n)) * 100;
    out.push({
      clave: d.clave,
      gravedad: porciento > d.umbral ? 'media' : 'info',
      titulo: d.titulo(ps.length),
      quePasa: d.quePasa,
      porQueAfecta: d.porQue,
      queHacer: d.queHacer,
      piezas: ps.length,
      ids: topeIds(ps.filter((x) => x.i.centro).map((x) => x.id)),
      tipos: [...new Set(ps.map((x) => x.nombre))],
      sinForma: ps.filter((x) => !x.i.centro).length || undefined,
      impacto: null,
    });
  }
  return out;
}

// ───────────────────────────── orquestador ─────────────────────────────

const ORDEN: Record<GravedadDeAviso, number> = { alta: 0, media: 1, info: 2 };

/** Corre todos los chequeos. Nunca tira: uno que falla simplemente no aparece. */
export function diagnosticarModelo(e: EntradaDiagnosticoModelo, p: ParametrosChequeoPlano): DiagnosticoDelModelo {
  const avisos: AvisoDelModelo[] = [];
  const seguro = (f: () => AvisoDelModelo | AvisoDelModelo[] | null) => {
    try {
      const r = f();
      if (Array.isArray(r)) avisos.push(...r);
      else if (r) avisos.push(r);
    } catch {
      /* un chequeo que falla no tumba a los demás */
    }
  };
  seguro(() => piezasRepetidasModelo(e, p));
  seguro(() => mismaIdentificacion(e, p));
  seguro(() => piezasSinMedida(e, p));
  seguro(() => piezasDesmedidas(e, p));
  seguro(() => piezasLejos(e, p));
  seguro(() => loQueElModeloNoDice(e, p));
  avisos.sort((a, b) => ORDEN[a.gravedad] - ORDEN[b.gravedad] || b.piezas - a.piezas);
  let piezasRevisadas = 0;
  try {
    piezasRevisadas = piezasDelModelo(e.inventario).length;
  } catch {
    /* sin inventario legible: no hay nada que contar */
  }
  return { avisos, piezasRevisadas };
}
